import BoltIcon from "@mui/icons-material/Bolt";
import CloseIcon from "@mui/icons-material/Close";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import LayersIcon from "@mui/icons-material/Layers";
import PlagiarismIcon from "@mui/icons-material/Plagiarism";
import { useEffect, useState } from "react";
import { Button, Carousel, Modal } from "react-bootstrap";

import { useSelector } from "react-redux";
import { RootState } from "../../../../store/reducer/_store";
import { isOnboardingShown, storeOnboardingShown } from "../../../../utils";
import { useIsMobile } from "../../../../utils/_isMobile";
import "./index.scss";

type CardItem = {
  icon: React.ReactNode;
  text: string;
};

const CARDS: CardItem[] = [
  {
    icon: <LayersIcon fontSize="inherit" />,
    text: "Trabalha com alto volume de pesquisa e precisa de agilidade",
  },
  {
    icon: <HelpCenterIcon fontSize="inherit" />,
    text: "Já usou IA genérica e se deparou com fontes pouco confiáveis",
  },
  {
    icon: <BoltIcon fontSize="inherit" />,
    text: "Precisa de velocidade na criação de peças e documentos jurídicos",
  },
  {
    icon: <ContentPasteSearchIcon fontSize="inherit" />,
    text: "Quer centralizar as informações e pesquisas em um só lugar",
  },
  {
    icon: <PlagiarismIcon fontSize="inherit" />,
    text: "Precisa analisar e validar documentos com maior agilidade",
  },
];

function StepIntro() {
  return (
    <div className="lex-onboarding__step">
      <h2 className="lex-onboarding__title">
        A base jurídica mais completa,
        <strong>AGORA COM O PODER DA IA.</strong>
      </h2>

      <p className="lex-onboarding__lead">
        A Plataforma LEX reúne a base jurídica da LEX Editora para pesquisa,
        enquanto a LEX-IA usa essa mesma base para apoiar a criação, análise e
        organização do trabalho jurídico, tudo dentro do ecossistema LEX.
      </p>

      <div className="lex-onboarding__diagram">
        <div className="lex-onboarding__side-card lex-onboarding__side-card--red">
          <h4>Plataforma LEX</h4>
          <span>Pesquisa com segurança</span>
          <p>
            Aqui você pesquisa legislação, jurisprudência, doutrina e modelos
            com filtros avançados.
          </p>
        </div>

        <div className="lex-onboarding__ring">
          <svg viewBox="0 0 240 240" aria-hidden="true">
            <circle
              cx="120"
              cy="120"
              r="112"
              fill="none"
              stroke="#e2ddd4"
              strokeWidth="1.5"
              strokeDasharray="2 6"
            />
            <path
              className="lex-onboarding__arc lex-onboarding__arc--red"
              d="M120 24 A96 96 0 0 0 120 216"
              fill="none"
              stroke="#a0181b"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              className="lex-onboarding__arc lex-onboarding__arc--gold"
              d="M120 24 A96 96 0 0 1 120 216"
              fill="none"
              stroke="#c0a15e"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
          <div className="lex-onboarding__ring-logo">
            <img src="/images/logo_home.png" alt="LEX Home" />
          </div>
        </div>

        <div className="lex-onboarding__side-card lex-onboarding__side-card--gold">
          <h4>LEX-IA</h4>
          <span>Continuidade do trabalho</span>
          <p>
            Utilize para criar peças, analisar documentos, mantendo o contexto e
            a segurança da LEX Editora.
          </p>
        </div>
      </div>
    </div>
  );
}

function CardTile({ item }: { item: CardItem }) {
  return (
    <div className="lex-onboarding__card">
      {item.icon}
      <p>{item.text}</p>
    </div>
  );
}

function StepCards() {
  const isMobile = useIsMobile();

  return (
    <div className="lex-onboarding__step">
      <h3 className="lex-onboarding__step-title">
        A <em>LEX-IA</em> foi feita para você que:
      </h3>

      {isMobile ? (
        <Carousel
          className="lex-onboarding__carousel"
          interval={null}
          indicators
          variant="dark"
        >
          {CARDS.map((item, i) => (
            <Carousel.Item key={i}>
              <div className="lex-onboarding__cards">
                <CardTile item={item} />
              </div>
            </Carousel.Item>
          ))}
        </Carousel>
      ) : (
        <div className="lex-onboarding__cards">
          {CARDS.map((item, i) => (
            <CardTile key={i} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

const TOTAL_STEPS = 2;

export default function OnboardingModal() {
  const loggedUser = useSelector(
    (state: RootState) => state.sessionContext.loggedUser,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);

  const close = () => {
    if (loggedUser) {
      storeOnboardingShown(loggedUser);
    }
    setIsOpen(false);
  };

  const isLastStep = step === TOTAL_STEPS - 1;

  const handleNext = () => {
    if (isLastStep) {
      close();
    } else {
      setStep((s) => s + 1);
    }
  };

  useEffect(() => {
    if (!loggedUser) {
      setIsOpen(false);
      return;
    }

    const shouldShow = !isOnboardingShown(loggedUser);
    setIsOpen(shouldShow);
  }, [loggedUser]);

  useEffect(() => {
    if (isOpen) setStep(0);
  }, [isOpen]);

  return (
    <Modal
      show={isOpen}
      onHide={close}
      centered
      size="lg"
      backdrop="static"
      dialogClassName="lex-onboarding modal-dialog-centered"
      className="lex-onboarding"
      aria-labelledby="lex-onboarding-title"
    >
      <Modal.Body>
        <div className="lex-onboarding__topbar">
          <div className="lex-onboarding__dots" role="tablist">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <button
                key={i}
                type="button"
                className={`lex-onboarding__dot${
                  i === step ? " is-active" : ""
                }`}
                aria-label={`Ir para etapa ${i + 1}`}
                aria-selected={i === step}
                role="tab"
                onClick={() => setStep(i)}
              />
            ))}
          </div>

          <button
            type="button"
            className="lex-onboarding__close"
            aria-label="Fechar"
            onClick={close}
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </button>
        </div>

        {step === 0 ? <StepIntro /> : <StepCards />}

        <div className="lex-onboarding__footer">
          {step > 0 ? (
            <button
              type="button"
              className="lex-onboarding__skip"
              onClick={() => setStep((s) => s - 1)}
            >
              Voltar
            </button>
          ) : (
            <button
              type="button"
              className="lex-onboarding__skip"
              onClick={close}
            >
              Pular
            </button>
          )}

          <Button className="lex-onboarding__cta" onClick={handleNext}>
            {isLastStep ? "Fechar" : "Continuar"}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
